import React from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Dropdown } from "semantic-ui-react";
import Status from "components/ui/status";

import "./MultiSelectInput.css";

/**
 * This component is a select input for which we could select multiple value and add a customized value.
 */
const MultiSelectInput = ({
	search,
	allowAdditions,
	additionLabel,
	options,
	values,
	defaultValue,
	onChange,
	onAddItem,
	error,
	disabled,
	placeholder,
	...rest
}) => {
	const { t } = useTranslation();

	const onInternalValueChange = React.useCallback(
		(_, data) => {
			onChange(data);
		},
		[onChange]
	);

	const renderOption = option => ({
		key: option.value,
		text: option.text,
		content: (
			<div
				className={`dropdown-option ${
					option.disabled ? "disabled-option" : ""
				}`}
			>
				<span>{option.text}</span>
				{option.status !== undefined && (
					<Status status={option.status} />
				)}
			</div>
		),
		value: option.value,
		disabled: option.disabled
	});

	// Map options to use renderOption
	const renderedOptions = options.map(renderOption);

	// Customize rendering of selected labels to display name only
	const renderSelectedLabel = label => {
		const originalOption = options.find(
			option => option.value === label.value
		);
		const eventName = originalOption ? originalOption.text : label.text;

		return {
			content: eventName,
			key: label.value
		};
	};

	return (
		<Dropdown
			/* eslint-disable-next-line react/jsx-props-no-spreading */
			options={renderedOptions}
			value={values}
			allowAdditions={allowAdditions}
			additionLabel={allowAdditions ? additionLabel : null}
			search={search}
			fluid
			multiple
			error={error}
			selection
			onChange={onInternalValueChange}
			onAddItem={onAddItem}
			noResultsMessage={t(
				"access.event.list.multiselectinput.no_results"
			)}
			disabled={disabled}
			renderLabel={renderSelectedLabel}
			placeholder={placeholder}
			{...rest}
		/>
	);
};

MultiSelectInput.propTypes = {
	/**
	 * Whether we allow to search a value in the input among the options.
	 */
	search: PropTypes.bool,
	/**
	 * Whether we allow to search a value in the input among the options.
	 */
	allowAdditions: PropTypes.bool,
	/**
	 * The 'add' label to display for the addition value (if allowAdditions is true).
	 */
	additionLabel: PropTypes.string,
	/**
	 * List of object that stores the options available in the selector.
	 */
	options: PropTypes.arrayOf(PropTypes.object),
	/**
	 * List of object that stores the options available in the selector.
	 */
	values: PropTypes.arrayOf(PropTypes.any),
	/**
	 * List of object that stores the default values.
	 */
	defaultValue: PropTypes.arrayOf(PropTypes.any),
	/**
	 * The function to trigger when the data value change.
	 */
	onChange: PropTypes.func,
	/**
	 * The function to trigger when we add a new value (if allowAdditions true).
	 * The function take the typed value from the input and must return an option object ({ text, value }) or null.
	 */
	onAddItem: PropTypes.func,
	/**
	 * Information about that field has errors (for example, validation error)
	 */
	error: PropTypes.bool,
	/**
	 * Field is disabled
	 */
	disabled: PropTypes.bool,
	/**
	 * The placeholder of the field to display
	 */
	placeholder: PropTypes.string
};

MultiSelectInput.defaultProps = {
	search: true,
	allowAdditions: false,
	additionLabel: null,
	options: [],
	values: [],
	defaultValue: [],
	onChange: null,
	onAddItem: null,
	error: false,
	disabled: false,
	placeholder: "Select options..."
};

export default MultiSelectInput;
